import {
  ArrowRightIcon,
  CaretDownIcon,
  MapPinIcon,
} from '@phosphor-icons/react'
import { CaretLeftIcon } from '@phosphor-icons/react/dist/ssr'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { DialogClose } from '@radix-ui/react-dialog'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import axios from 'axios'
import { useEffect, useState } from 'react'
import useSWR from 'swr'
import { MyBLINK, SwissPlaceSearchResult } from '@/src/@types/blinkadmin'
import { fullFlchUrl } from '@/src/BlinkAdminApiClient'
import Button from '@/src/components/core/Button'
import { DialogBody, DialogHeader } from '@/src/components/core/Dialog'
import ErrorMessage from '@/src/components/core/ErrorMessage'
import Map from '@/src/components/elements/Map'
import Marker from '@/src/components/elements/Marker'
import { useBookingContext } from '@/src/components/flchBooking/locationContext/BookingContext'
import PlaceSearch from '@/src/components/flchBooking/locationContext/PlaceSearch'

export default function Location() {
  const { location, address, confirmAddress } = useBookingContext()

  const [selectedPlace, setSelectedPlace] =
    useState<SwissPlaceSearchResult | null>(null)
  const [selectedLocation, setSelectedLocation] =
    useState<MyBLINK.BookableLocation | null>(location)
  const [selectedAddress, setSelectedAddress] =
    useState<MyBLINK.BookableAddress | null>(address)
  const [accordionSelected, setAccordionSelected] = useState<
    string | undefined
  >(address?.id.toString())
  const [error, setError] = useState<string | null>(null)

  const { data: locations, isLoading: locationsLoading } = useSWR<
    MyBLINK.BookableLocation[]
  >(
    selectedPlace && `booking/places/${selectedPlace.id}/locations`,
    async () =>
      (
        await axios.get(
          fullFlchUrl(`booking/places/${selectedPlace!.id}/locations`)
        )
      ).data
  )

  const { data: addresses, isLoading: addressesLoading } = useSWR<
    MyBLINK.BookableAddress[]
  >(
    selectedLocation &&
      `booking/locations/${selectedLocation.id}/addresses?type=pickup`,
    async () =>
      (
        await axios.get(
          fullFlchUrl(
            `booking/locations/${selectedLocation!.id}/addresses?type=pickup`
          )
        )
      ).data
  )

  useEffect(() => {
    if (!selectedAddress && addresses && addresses.length === 1) {
      setSelectedAddress(addresses[0])
      setAccordionSelected(addresses[0].id.toString())
    }
  }, [addresses, selectedAddress])

  const confirmSelection = () => {
    if (!selectedLocation || !selectedAddress) {
      setError('Bitte wähle einen Treffpunkt aus.')
      return
    }
    confirmAddress(selectedLocation, selectedAddress)
  }

  return (
    <>
      <DialogHeader title="Wo sollen wir uns treffen?" />
      <DialogBody>
        {selectedLocation === null && (
          <>
            <PlaceSearch
              selectedPlace={selectedPlace}
              setSelectedPlace={setSelectedPlace}
            />
            {(selectedPlace && (
              <div className="mt-8">
                <p className="mb-4">Standorte in der Umgebung</p>
                {locationsLoading && !locations?.length && (
                  <p className="flex items-center justify-center space-x-2 py-2">
                    <svg
                      className="size-4 animate-spin text-blue-800"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Suche...</span>
                  </p>
                )}
                <div className="space-y-3">
                  {locations?.map((location) => (
                    <button
                      key={location.id}
                      onClick={() => {
                        setSelectedLocation(location)
                        setSelectedAddress(null)
                      }}
                      className="flex w-full items-center justify-between rounded-sm border border-blue-125 bg-blue-25 px-3 py-4 transition-colors duration-150 hover:bg-blue-100"
                    >
                      <div className="flex items-center">
                        <MapPinIcon
                          size={20}
                          weight="fill"
                          className="shrink-0 opacity-80"
                        />
                        <span className="ml-1 font-bold">{location.name}</span>
                        <span className="ml-2">{location.distance} km</span>
                      </div>
                      <ArrowRightIcon
                        size={20}
                        weight="bold"
                        className="shrink-0 text-blue-600"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )) || <div className="h-56" />}
          </>
        )}
        {selectedLocation && (
          <>
            <button
              onClick={() => {
                setSelectedLocation(null)
                setSelectedAddress(null)
                setAccordionSelected(undefined)
                setError(null)
              }}
              className="flex items-center space-x-2 text-blue-700"
            >
              <CaretLeftIcon size={20} weight="bold" />
              <span className="text-sm font-bold">
                Zurück zur Standortsuche
              </span>
            </button>
            <p className="mt-8">
              Unsere Treffpunkte in {selectedLocation.name}
            </p>
            {addressesLoading && !addresses?.length && (
              <p className="flex items-center justify-center space-x-2 py-2">
                <svg
                  className="size-4 animate-spin text-blue-800"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Suche...</span>
              </p>
            )}
            <RadioGroupPrimitive.Root
              value={selectedAddress?.id.toString()}
              onValueChange={(addressId) => {
                setSelectedAddress(
                  addresses?.find((a) => a.id.toString() === addressId) ?? null
                )
                setAccordionSelected(addressId)
                setError(null)
              }}
            >
              <AccordionPrimitive.Root type="single" value={accordionSelected}>
                {addresses?.map((address) => (
                  <AccordionPrimitive.Item
                    key={address.id}
                    value={address.id.toString()}
                    className="group border-b border-blue-100 py-4"
                  >
                    <RadioGroupPrimitive.Item
                      value={address.id.toString()}
                      className="group flex w-full items-center justify-between"
                    >
                      <div className="flex items-center text-left">
                        <div className="box-border size-5 shrink-0 rounded-full border-2 border-blue-700 group-data-[state=checked]:border-[6px]" />
                        <span className="ml-3 grow font-bold">
                          {address.name}
                        </span>
                      </div>
                      <CaretDownIcon
                        size={20}
                        weight="bold"
                        className="ml-4 shrink-0 text-blue-700 transition-transform duration-150 group-data-[state=open]:rotate-180"
                      />
                    </RadioGroupPrimitive.Item>
                    <AccordionPrimitive.Content>
                      {address.longitude && address.latitude && (
                        <div className="aspect-w-3 relative mt-2 w-full overflow-hidden rounded-[5px] aspect-h-2 lg:aspect-w-5 lg:aspect-h-2">
                          <div>
                            <Map
                              markers={[[address.longitude, address.latitude]]}
                              interactive={false}
                              className="size-full"
                            >
                              <Marker
                                position={{
                                  lat: address.latitude,
                                  lng: address.longitude,
                                }}
                                icon="flch-pickup"
                                size="md"
                              />
                            </Map>
                          </div>
                        </div>
                      )}
                    </AccordionPrimitive.Content>
                  </AccordionPrimitive.Item>
                ))}
              </AccordionPrimitive.Root>
            </RadioGroupPrimitive.Root>
            <div className="mt-8 flex flex-col items-end space-y-2">
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <div className="flex space-x-4">
                <DialogClose asChild>
                  <Button version="gray-transparent">Abbrechen</Button>
                </DialogClose>
                <Button version="blue" onClick={confirmSelection}>
                  Standort auswählen
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogBody>
    </>
  )
}
